/*
 * @prop --component-teaser-color-border: Color of teaser border
 * @prop --component-teaser-box-color-background: Background color of teaser box
 * @prop --component-teaser-box-color-text: Color of teaser box text
 * @prop --component-teaser-box-color-headline: Color of teaser box headline
*/

@import '../../global/utility/responsive/responsive-mixins';

$teaser-img-hover-op-white: rgba($ui-white, .1);

// side paddings
$padding-side: 20px;
$padding-side-medium: 40px;
$padding-side-desktop: 60px;
// top paddings
$padding-top: 12px;
$padding-top-medium: 32px;
$padding-top-desktop: $padding-top-medium;

// bottom paddings
$padding-bottom: 14px;
$padding-bottom-medium: 34px;
$padding-bottom-desktop: $padding-bottom-medium;

// responsive behaviour
$size-desktop: 933;
$size-medium: 700;
$size-small: 546;
$sizes: $size-desktop, $size-medium, $size-small;

@mixin teaser-hover($img-layer: $teaser-img-hover-op-white) {
  .teaser__figure {
    @include teaser-hover-image($img-layer);

    position: relative;
  }
}

// teaser image hover layer
@mixin teaser-hover-image($img-layer: $teaser-img-hover-op-white) {
  &::before {
    background-color: $img-layer;
    content: '';
    display: block;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: $z-index-teaser-hover-figure;
  }
}

:host {
  @include responsive($sizes);

  display: block;
  position: relative;
  z-index: 0; // Creating an own z-index-scope prevents children of this element overlapping other elements

  .teaser {
    height: 100%;

    @include resetCurrentColors();

    > div {
      box-sizing: border-box;
      height: 100%;
    }

    .is-linked {
      cursor: pointer;
    }

    .teaser__icon {
      display: inline;
    }

    &--align-button {
      > div {
        display: flex;
        flex-direction: column;
      }

      .teaser__body {
        display: flex;
        flex: 1;
        flex-direction: column;
      }

      .teaser__link {
        margin-top: auto;
        padding-top: 19px;
      }

      .teaser__link-list {
        margin-top: auto;
        padding-top: 19px;

        .teaser__link {
          margin-top: 0;
          padding-top: 0;
        }
      }

      .button-wrapper {
        display: inline-flex;
        margin-top: auto;
      }
    }

    &__copyright {
      @extend %caption;

      color: $ui-grey-plus-1;
    }

    &__headline::part(headline) {
      @extend %headlineH3;

      color: var(--component-teaser-box-color-headline var(--skin-color-text, $ui-dark));
      margin: 0;
    }

    &__link {
      margin-top: 12px;

      &--btn {
        margin-top: 19px;
      }
    }

    &__text {
      margin: 0;
      margin-top: 12px;
    }

    &__meta {
      @extend %caption;

      margin: 18px 0 3px;
      padding: 0;

      li {
        display: inline-block;

        &:not(:last-child)::after {
          content: '|';
          margin: 0 5px;
        }
      }

      &:last-child {
        margin-bottom: 3px;
      }
    }

    &__figure {
      margin: 0;

      img {
        vertical-align: top;
        width: 100%;
      }
    }

    &__body {
      padding-bottom: $padding-bottom;
      padding-top: $padding-top;
    }

    &__link-list {
      list-style: none;
      margin: 19px 0 0;
      padding: 0;
    }

    &__link-list-item {
      display: block;
      margin-bottom: 20px;

      &:last-child {
        margin-bottom: 0;
      }

      ui-link {
        margin-top: 0;
      }
    }

    &[class*='teaser--box-'] {
      .teaser__body,
      .teaser__copyright {
        padding-left: $padding-side;
        padding-right: $padding-side;
      }
    }

    &.no-indentation {
      .teaser__body,
      .teaser__copyright {
        padding-left: 0;
        padding-right: 0;
      }
    }

    &--box-white {
      > div {
        background-color: var(--component-teaser-box-color-background, var(--skin-color-white, $ui-white));

        &.is-linked:hover {
          @include teaser-hover();
        }
      }

      &.teaser--box-border {
        > div {
          border: 1px solid var(--component-teaser-color-border, var(--skin-color-secondary, $ui-grey));
        }
      }

      .ui-headline::part(headline) {
        color: var(--component-teaser-box-color-headline, var(--skin-color-text, $ui-dark));
      }

      .teaser__text,
      .teaser__meta,
      .teaser__copyright {
        color: var(--component-teaser-box-color-text, var(--skin-color-text, $ui-dark));
      }
    }

    &--box-transparent {
      > div {
        background-color:
          var(
            --component-teaser-box-color-background,
            var(--skin-color-secondary-lighter, $ui-grey-minus-3)
          );
        color: var(--component-teaser-box-color-text, var(--skin-color-text, $ui-dark));

        .ui-headline::part(headline) {
          color: var(--component-teaser-box-color-headline, var(--skin-color-text, $ui-dark));
        }
      }

      .is-linked:hover {
        @include teaser-hover();
      }

      @include breakpoint($medium) {
        .is-linked:hover {
          .teaser__body,
          .teaser__copyright {
            background-color: rgba($ui-white, .75);
          }

          .teaser__figure {
            position: static;
          }
        }

        > div {
          background-color: rgba($ui-white, .85);
          overflow: hidden;
          position: relative;
        }

        .teaser__image {
          height: 100%;
          position: absolute;
          width: 100%;
          min-height: 100%;
          min-width: 100%;
        }

        .teaser__copyright {
          background-color: rgba($ui-white, .85);
          color: $ui-grey-plus-2;
          margin-top: 56.3%;
          position: relative;
          z-index: $z-index-teaser-body;

          + .teaser__body {
            margin: 0;
          }
        }

        .teaser__body {
          background-color: rgba($ui-white, .85);
          height: 100%;
          margin-top: 56.3%;
          position: relative;
          z-index: $z-index-teaser-body;
        }
      }

      &.teaser--no-media {
        .teaser__body {
          margin-top: 0;
        }
      }
    }

    &--box-grey {
      color: var(--component-teaser-box-color-text, var(--skin-color-text, $ui-dark));

      .ui-headline::part(headline) {
        color: var(--component-teaser-box-color-headline, var(--skin-color-text, $ui-dark));
      }

      > div {
        background-color:
          var(
            --component-teaser-box-color-background,
            var(--skin-color-secondary-lighter, $ui-grey-minus-3)
          );

        &.is-linked:hover {
          @include teaser-hover();
        }
      }
    }

    &--box-light-blue {
      color: var(--component-teaser-box-color-text, var(--skin-color-text, $ui-dark));

      .ui-headline::part(headline) {
        color: var(--component-teaser-box-color-headline, var(--skin-color-text, $ui-dark));
      }

      > div {
        background-color:
          var(
            --component-teaser-box-color-background,
            var(--skin-color-primary-lighter, $ui-blue-minus-5)
          );

        &.is-linked:hover {
          @include teaser-hover();
        }
      }
    }

    &--box-dark-blue {
      @include invertCurrentColors();

      > div {
        background-color: var(--component-teaser-box-color-background, var(--skin-color-primary, $ui-blue));

        &.is-linked:hover {
          @include teaser-hover();
        }
      }

      .ui-headline::part(headline) {
        color: var(--component-teaser-box-color-headline, var(--skin-color-primary-inverted, $ui-white));
      }

      .teaser__text,
      .teaser__meta,
      .teaser__copyright {
        color: var(--component-teaser-box-color-text, var(--skin-color-primary-inverted, $ui-white));
      }
    }

    .teaser__figure + .teaser__copyright {
      padding-top: $padding-top;

      + .teaser__body {
        padding-top: 8px;
      }
    }
  }

  mark {
    @extend %mark;
  }

  @extend %subsup;
}

// responsive styles for component

@include min-width($sizes, $size-small) {
  .teaser {
    &--float-img {
      > div {
        @include clearfix();
      }

      &[class*='teaser--box'] {
        > div {
          padding: $padding-side $padding-side $padding-bottom;
        }
      }

      .teaser__figure {
        float: left;
        margin: 0 30px 0 0;
        width: calc(50% - 15px);

        + .teaser__copyright {
          padding: 0 30px 3px 0;
        }
      }

      .teaser__copyright {
        float: left;
        width: calc(50% - 15px);

        + .teaser__body {
          padding-top: 0;
        }
      }

      .teaser__body {
        /* stylelint-disable */
        padding: 0 !important;
      }

      .teaser__headline {
        margin-top: -8px;
      }
    }
  }
}

@include min-width($sizes, $size-medium) {
  .teaser {
    &__body {
      padding-bottom: $padding-bottom-medium;
      padding-top: $padding-top-medium;
    }

    &[class*='teaser--box-'] {
      .teaser__body,
      .teaser__copyright {
        padding-left: $padding-side-medium;
        padding-right: $padding-side-medium;
      }
    }

    &.no-indentation {
      .teaser__body,
      .teaser__copyright {
        padding-left: 0;
        padding-right: 0;
      }
    }

    &--float-img {
      &[class*='teaser--box'] {
        > div {
          padding: $padding-side-medium;
        }

        .teaser__copyright {
          padding: 0 30px 3px 0;
          margin-top: 12px;
        }
      }

      &[class*='teaser--box-white']:not([class*='teaser--box-border']) {
        > div {
          padding: 0;
        }
      }
    }
  }
}

@include min-width($sizes, $size-desktop) {
  .teaser {
    &__body {
      padding-bottom: $padding-bottom-desktop;
      padding-top: $padding-top-desktop;
    }

    &[class*='teaser--box-'] {
      .teaser__body,
      .teaser__copyright {
        padding-left: $padding-side-desktop;
        padding-right: $padding-side-desktop;
      }
    }

    &.no-indentation {
      .teaser__body,
      .teaser__copyright {
        padding-left: 0;
        padding-right: 0;
      }
    }

    &--float-img {
      &[class*='teaser--box'] {
        > div {
          padding: $padding-side-desktop;
        }

        .teaser__copyright {
          padding: 0 30px 3px 0;
        }
      }

      &[class*='teaser--box-white']:not([class*='teaser--box-border']) {
        > div {
          padding: 0;
        }
      }
    }
  }
}
